
  import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { Button, Icon, Typography } from '@voximplant/spaceui';
  import { useStore } from 'effector-vue/composition';
  import { $settings } from '@/store/settings';
  import {
    $callDuration,
    $calls,
    hangUp,
    currentSelectCall,
    $currentSelectCallId,
  } from '@/store/calls';
  import Loading from '@/components/animation/Loading.vue';
  import { changeVideoParam } from '@/lib/sdkSource';
  import UserWithoutVideo from '@/components/calls/UserWithoutVideo.vue';
  import VideoCallFooter from '@/components/calls/VideoCallFooter.vue';
  import { timeFormat } from '@/lib/Helpers';
  import { useI18n } from 'vue-i18n';
  import DraggableBlock from '@/components/DraggableBlock.vue';

  const __default__ = defineComponent({
    name: 'VideoCall',
    components: {
      Loading,
      Typography,
      Button,
      Icon,
      UserWithoutVideo,
      VideoCallFooter,
      DraggableBlock,
    },
    setup() {
      const { t } = useI18n();
      const settings = useStore($settings);
      const callDuration = useStore($callDuration);
      const allCalls = useStore($calls);
      const currentSelectCallId = useStore($currentSelectCallId);
      const selectCall = useStore(currentSelectCall);
      const draggable = ref<HTMLDivElement | null>(null);
      const isTeleportVideo = ref(false);

      watch(draggable, () => {
        isTeleportVideo.value = true;
      });

      // flags
      const isCallConnected = computed(() => {
        return allCalls.value[currentSelectCallId.value]?.call.signalingConnected;
      });
      const isRemoteVideoMute = computed(() => {
        if (
          !allCalls.value[currentSelectCallId.value] ||
          !isCallConnected.value ||
          !selectCall.value?.active ||
          allCalls.value[selectCall.value?.id]?.params.remotePausedState
        ) {
          return true;
        }
        return !allCalls.value[currentSelectCallId.value]?.params.remoteVideo;
      });
      const isLocalVideoMute = computed(
        () => settings.value.videoMute || !selectCall.value?.active
      );
      const isRemoteMute = computed(() => allCalls.value[currentSelectCallId.value]?.params.muted);
      const isWindowBlock = computed(
        () =>
          isLocalVideoMute.value &&
          isRemoteVideoMute.value &&
          outgoingBlockClasses.value['hide-block']
      );
      const needTeleport = computed(() => {
        return (
          (!isLocalVideoMute.value && isRemoteVideoMute.value && !settings.value.maximize) ||
          settings.value.maximize
        );
      });

      // classes
      const incomingVideoClasses = computed(() => ({
        'show-all-container': !isRemoteVideoMute.value && !settings.value.maximize,
        'maximize-container': settings.value.maximize,
        'second-column': videoContainerClasses.value['one-column-video'], // center when one column
      }));

      const incomingBlockClasses = computed(() => ({
        'show-all-container': isWindowBlock.value && !settings.value.maximize,
        'show-to-window':
          !isLocalVideoMute.value && isRemoteVideoMute.value && !settings.value.maximize,
        'hide-video':
          isLocalVideoMute.value &&
          isRemoteVideoMute.value &&
          !outgoingBlockClasses.value['hide-block'],
        'maximize-block': settings.value.maximize && !settings.value.sharing,
        'maximize-block_sharing': settings.value.maximize && settings.value.sharing,
        'second-column': videoContainerClasses.value['one-column-video'], // center when one column
      }));

      const outgoingVideoClasses = computed(() => ({
        'show-all-container':
          !isLocalVideoMute.value && isRemoteVideoMute.value && !settings.value.maximize,
        'maximize-container': settings.value.maximize,
      }));

      const outgoingBlockClasses = computed(() => ({
        'hide-block': isLocalVideoMute.value,
      }));

      const videoContainerClasses = computed(() => ({
        'layout-local-video': selectCall.value?.status === 'CONNECTED' && !settings.value.maximize,
        'layout-remote-video': selectCall.value?.status !== 'CONNECTED' && !settings.value.maximize,
        'layout-maximize-video': settings.value.maximize,
        'layout-full-screen': settings.value.fullscreen,
        'one-column-video': settings.value.maximize && isLocalVideoMute.value,
        'two-column-video':
          !isLocalVideoMute.value && settings.value.maximize && !settings.value.sharing,
        sharing: settings.value.maximize && settings.value.sharing,
        'remote-sharing': settings.value.maximize && settings.value.remoteSharing,
        'full-sharing':
          settings.value.maximize && settings.value.remoteSharing && isLocalVideoMute.value,
      }));
      // local and remote video object fit computed
      const videoObjectFit = computed(() =>
        settings.value.maximize || settings.value.sharing ? 'contain' : 'cover'
      );
      const incomingVideoObjectFit = computed(() =>
        settings.value.remoteSharing ? 'contain' : 'cover'
      );

      onMounted(() => {
        let isLocalVideoEnable;
        if (selectCall.value) {
          isLocalVideoEnable =
            allCalls.value[selectCall.value?.id]?.call.settings.videoDirections.sendVideo;
          if (isLocalVideoEnable) changeVideoParam(true);
        }
      });

      onBeforeUnmount(() => {
        changeVideoParam(false);
      });

      return {
        t,
        settings,
        selectCall,
        callDuration,
        isRemoteVideoMute,
        isLocalVideoMute,
        videoContainerClasses,
        incomingVideoClasses,
        incomingBlockClasses,
        outgoingVideoClasses,
        outgoingBlockClasses,
        isWindowBlock,
        isRemoteMute,
        timeFormat,
        hangUp,
        draggable,
        isTeleportVideo,
        needTeleport,
        isCallConnected,
        videoObjectFit,
        incomingVideoObjectFit,
      };
    },
  });

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "4d1219c7": (_ctx.incomingVideoObjectFit),
  "30e51a01": (_ctx.videoObjectFit)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__